import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../services/api';
import '../styles/CreateAnnonce.css';


// Compress image client-side before upload (max 2000px, quality 92%)
const compressImage = (file, maxSize = 2000, quality = 0.92) => {
  return new Promise((resolve) => {
    // If file is already small (< 500KB), skip compression
    if (file.size < 500 * 1024) {
      return resolve(file);
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Only resize if larger than maxSize
      if (width <= maxSize && height <= maxSize) {
        // Still re-encode to JPEG to reduce size
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          quality
        );
        return;
      }

      // Calculate new dimensions
      if (width > height) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      } else {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fallback to original on error
    };

    img.src = url;
  });
};

const CreateProduct = ({ vehicleMode = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    original_price: '',
    category_id: '',
    subcategory_id: '',
    size: '',
    brand: '',
    model: '',
    year: '',
    color: '',
    material: '',
    condition: 'bon',
    street: '',
    city: '',
    postal_code: '',
    shipping_available: false,
    shipping_price: '',
    local_pickup: false,
    // Vehicle fields
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_mileage: '',
    vehicle_fuel: '',
    vehicle_transmission: '',
    vehicle_power_cv: '',
    vehicle_displacement: '',
    vehicle_doors: '',
    vehicle_seats: '',
    vehicle_drive: '',
    vehicle_color_interior: '',
    vehicle_inspection_date: '',
    vehicle_vin: ''
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [mainCategories, setMainCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  // Reinitialiser le formulaire quand on change de route (article <-> vehicule)
  useEffect(() => {
    setFormData({
      title: '',
      description: '',
      price: '',
      original_price: '',
      category_id: '',
      subcategory_id: '',
      size: '',
      brand: '',
      model: '',
      year: '',
      color: '',
      material: '',
      condition: 'bon',
      street: '',
      city: '',
      postal_code: '',
      shipping_available: false,
      shipping_price: '',
      local_pickup: false,
      vehicle_make: '',
      vehicle_model: '',
      vehicle_year: '',
      vehicle_mileage: '',
      vehicle_fuel: '',
      vehicle_transmission: '',
      vehicle_power_cv: '',
      vehicle_displacement: '',
      vehicle_doors: '',
      vehicle_seats: '',
      vehicle_drive: '',
      vehicle_color_interior: '',
      vehicle_inspection_date: '',
      vehicle_vin: ''
    });
    setImages([]);
    setImagePreviews([]);
    setMainImageIndex(0);
    setSubcategories([]);
    setError('');
    setFieldErrors({});
    setTouched({});
  }, [location.pathname]);

  // Charger les categories hierarchiques
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await productsAPI.getCategoriesHierarchy();
        setMainCategories(response.data);
        // Auto-select Voitures category in vehicle mode
        if (vehicleMode) {
          const voituresCat = response.data.find(c => c.slug === 'voitures');
          if (voituresCat) {
            setFormData(prev => ({ ...prev, category_id: voituresCat.id.toString() }));
          }
        }
      } catch (err) {
        console.error('Erreur chargement categories:', err);
      }
    };
    fetchCategories();
  }, [vehicleMode, location.pathname]);

  // Mettre a jour les sous-categories quand la categorie principale change
  useEffect(() => {
    if (formData.category_id) {
      const selectedCategory = mainCategories.find(c => c.id.toString() === formData.category_id);
      if (selectedCategory && selectedCategory.subcategories) {
        setSubcategories(selectedCategory.subcategories);
      } else {
        setSubcategories([]);
      }
      setFormData(prev => ({ ...prev, subcategory_id: '' }));
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id, mainCategories]);

  // Determiner si c'est la categorie voitures (hors accessoires)
  const isVehicleCategory = () => {
    const selectedCat = mainCategories.find(c => c.id.toString() === formData.category_id);
    if (!selectedCat || selectedCat.slug !== 'voitures') return false;
    // Si sous-categorie "accessoires" selectionnee, formulaire normal
    if (formData.subcategory_id) {
      const selectedSub = subcategories.find(s => s.id.toString() === formData.subcategory_id);
      if (selectedSub && selectedSub.slug === 'voitures-accessoires') return false;
    }
    return true;
  };

  // Determiner si c'est une categorie vestimentaire (pour afficher le champ taille)
  const isClothingCategory = () => {
    const clothingCategories = ['femme', 'homme', 'enfant'];
    const selectedCat = mainCategories.find(c => c.id.toString() === formData.category_id);
    return selectedCat && clothingCategories.includes(selectedCat.slug);
  };

  // Determiner si c'est une categorie tech (pour afficher modele/annee)
  const isTechCategory = () => {
    const techCategories = ['informatique', 'jeux-video', 'electromenager'];
    const selectedCat = mainCategories.find(c => c.id.toString() === formData.category_id);
    return selectedCat && techCategories.includes(selectedCat.slug);
  };

  const isVehicle = isVehicleCategory();
  const maxImages = isVehicle ? 10 : 5;

  // Validation des champs
  const validateField = (name, value) => {
    switch (name) {
      case 'title':
        if (!value || value.trim() === '') return 'Le titre est obligatoire';
        if (value.length < 5) return 'Le titre doit contenir au moins 5 caracteres';
        if (value.length > 200) return 'Le titre ne peut pas depasser 200 caracteres';
        return '';
      case 'description':
        if (!value || value.trim() === '') return 'La description est obligatoire';
        if (value.length < 10) return 'La description doit contenir au moins 10 caracteres';
        return '';
      case 'price':
        if (!value || value === '') return 'Le prix est obligatoire';
        if (isVehicle && parseFloat(value) < 500) return t('vehicle.minPrice');
        if (!isVehicle && parseFloat(value) < 0.50) return 'Le prix minimum est de 0.50 CHF';
        return '';
      case 'category_id':
        if (!value || value === '') return 'La categorie est obligatoire';
        return '';
      default:
        return '';
    }
  };

  // Validation complete du formulaire
  const validateForm = () => {
    const errors = {};

    const titleError = validateField('title', formData.title);
    if (titleError) errors.title = titleError;

    const descError = validateField('description', formData.description);
    if (descError) errors.description = descError;

    const priceError = validateField('price', formData.price);
    if (priceError) errors.price = priceError;

    const categoryError = validateField('category_id', formData.category_id);
    if (categoryError) errors.category_id = categoryError;

    if (images.length === 0) {
      errors.images = 'Au moins une photo est obligatoire';
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData({
      ...formData,
      [name]: newValue
    });

    if (touched[name]) {
      const error = validateField(name, newValue);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const [compressing, setCompressing] = useState(false);

  const handleImageChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;

    // Verifier le nombre total
    const totalAfterAdd = images.length + newFiles.length;
    if (totalAfterAdd > maxImages) {
      setError(`Vous ne pouvez ajouter que ${maxImages - images.length} photo(s) supplementaire(s)`);
      return;
    }

    // Verifier la taille (15MB max par fichier)
    const tooBig = newFiles.find(f => f.size > 15 * 1024 * 1024);
    if (tooBig) {
      setError('Une image depasse 15MB. Veuillez choisir une image plus petite.');
      return;
    }

    setError('');
    setFieldErrors(prev => ({ ...prev, images: '' }));

    // Compress images before storing
    setCompressing(true);
    try {
      const compressedFiles = await Promise.all(newFiles.map(file => compressImage(file)));

      const combinedImages = [...images, ...compressedFiles];
      setImages(combinedImages);

      const newPreviews = compressedFiles.map(file => URL.createObjectURL(file));
      setImagePreviews([...imagePreviews, ...newPreviews]);
    } catch (err) {
      // Fallback: use original files if compression fails
      const combinedImages = [...images, ...newFiles];
      setImages(combinedImages);

      const newPreviews = newFiles.map(file => URL.createObjectURL(file));
      setImagePreviews([...imagePreviews, ...newPreviews]);
    }
    setCompressing(false);

    e.target.value = '';
  };

  const handleSetMainImage = (index) => {
    setMainImageIndex(index);
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    const newPreviews = [...imagePreviews];

    URL.revokeObjectURL(newPreviews[index]);

    newImages.splice(index, 1);
    newPreviews.splice(index, 1);

    setImages(newImages);
    setImagePreviews(newPreviews);

    if (mainImageIndex >= newImages.length) {
      setMainImageIndex(Math.max(0, newImages.length - 1));
    } else if (mainImageIndex > index) {
      setMainImageIndex(mainImageIndex - 1);
    }
  };

  // Calculer le prix total avec frais
  const calculateTotalPrice = () => {
    if (!formData.price || formData.price <= 0) return null;
    const price = parseFloat(formData.price);
    if (isVehicle) {
      return { price, insurance: 0, commission: 0, total: price, isVehicle: true };
    }
    const insurance = 1.5;
    const commission = price * 0.02; // 2%
    const total = price + insurance + commission;
    return { price, insurance, commission, total, isVehicle: false };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    setTouched({
      title: true,
      description: true,
      price: true,
      category_id: true,
      images: true
    });

    const errors = validateForm();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError('Veuillez corriger les erreurs ci-dessous');
      const firstErrorField = document.querySelector('.field-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Champs obligatoires
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('condition', formData.condition);
      formDataToSend.append('main_image_index', mainImageIndex);

      // Categorie
      const categoryToUse = formData.subcategory_id || formData.category_id;
      formDataToSend.append('category_id', categoryToUse);

      // Champs optionnels standard
      if (formData.original_price) formDataToSend.append('original_price', formData.original_price);
      if (formData.brand) formDataToSend.append('brand', formData.brand);
      if (formData.model) formDataToSend.append('model', formData.model);
      if (formData.year) formDataToSend.append('year', formData.year);
      if (formData.color) formDataToSend.append('color', formData.color);
      if (formData.material) formDataToSend.append('material', formData.material);
      if (formData.size) formDataToSend.append('size', formData.size);
      if (formData.street) formDataToSend.append('street', formData.street);
      if (formData.city) formDataToSend.append('city', formData.city);
      if (formData.postal_code) formDataToSend.append('postal_code', formData.postal_code);

      // Vehicle fields
      if (isVehicle) {
        // Forcer remise en main propre, pas d'envoi postal
        formDataToSend.append('shipping_available', '0');
        formDataToSend.append('shipping_price', '0');
        formDataToSend.append('local_pickup', '1');

        if (formData.vehicle_make) formDataToSend.append('vehicle_make', formData.vehicle_make);
        if (formData.vehicle_model) formDataToSend.append('vehicle_model', formData.vehicle_model);
        if (formData.vehicle_year) formDataToSend.append('vehicle_year', formData.vehicle_year);
        if (formData.vehicle_mileage) formDataToSend.append('vehicle_mileage', formData.vehicle_mileage);
        if (formData.vehicle_fuel) formDataToSend.append('vehicle_fuel', formData.vehicle_fuel);
        if (formData.vehicle_transmission) formDataToSend.append('vehicle_transmission', formData.vehicle_transmission);
        if (formData.vehicle_power_cv) formDataToSend.append('vehicle_power_cv', formData.vehicle_power_cv);
        if (formData.vehicle_displacement) formDataToSend.append('vehicle_displacement', formData.vehicle_displacement);
        if (formData.vehicle_doors) formDataToSend.append('vehicle_doors', formData.vehicle_doors);
        if (formData.vehicle_seats) formDataToSend.append('vehicle_seats', formData.vehicle_seats);
        if (formData.vehicle_drive) formDataToSend.append('vehicle_drive', formData.vehicle_drive);
        if (formData.vehicle_color_interior) formDataToSend.append('vehicle_color_interior', formData.vehicle_color_interior);
        if (formData.vehicle_inspection_date) formDataToSend.append('vehicle_inspection_date', formData.vehicle_inspection_date);
        if (formData.vehicle_vin) formDataToSend.append('vehicle_vin', formData.vehicle_vin);
      } else {
        formDataToSend.append('shipping_available', formData.shipping_available ? '1' : '0');
        formDataToSend.append('shipping_price', formData.shipping_available && formData.shipping_price ? formData.shipping_price : '0');
        formDataToSend.append('local_pickup', formData.local_pickup ? '1' : '0');
      }

      // Images - l'image principale en premier
      const reorderedImages = [...images];
      if (mainImageIndex > 0) {
        const mainImage = reorderedImages.splice(mainImageIndex, 1)[0];
        reorderedImages.unshift(mainImage);
      }

      reorderedImages.forEach(image => {
        formDataToSend.append('images', image);
      });

      await productsAPI.create(formDataToSend);
      navigate('/my-products');
    } catch (err) {
      console.error('Erreur creation article:', err.response?.data || err);
      const errorMsg = err.response?.data?.message || err.response?.data?.details || 'Erreur lors de la creation de l\'article';
      setError(errorMsg);
      setLoading(false);
    }
  };

  const getFieldClass = (fieldName) => {
    if (fieldErrors[fieldName]) return 'field-error';
    if (touched[fieldName] && !fieldErrors[fieldName]) return 'field-valid';
    return '';
  };

  const priceDetails = calculateTotalPrice();

  // Liste des marques populaires
  const carMakes = [
    'Audi', 'BMW', 'Citroen', 'Dacia', 'Fiat', 'Ford', 'Honda', 'Hyundai',
    'Kia', 'Land Rover', 'Mazda', 'Mercedes-Benz', 'Mini', 'Mitsubishi',
    'Nissan', 'Opel', 'Peugeot', 'Porsche', 'Renault', 'Seat', 'Skoda',
    'Subaru', 'Suzuki', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
  ];

  return (
    <div className="create-annonce-container">
      <div className="create-annonce-card">
        <h2>{isVehicle ? t('vehicle.createTitle') : t('product.createTitle')}</h2>
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          {/* Titre */}
          <div className={`form-group ${getFieldClass('title')}`}>
            <label>{t('product.titleLabel')}</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              onBlur={handleBlur}
              minLength="5"
              maxLength="200"
              placeholder={isVehicle ? t('vehicle.titlePlaceholder') : t('product.titlePlaceholder')}
              className={fieldErrors.title ? 'input-error' : ''}
            />
            {fieldErrors.title && <span className="error-text">{fieldErrors.title}</span>}
          </div>

          {/* Categorie principale (masquee en mode vehicule) */}
          {!vehicleMode && (
            <div className={`form-group ${getFieldClass('category_id')}`}>
              <label>{t('product.categoryLabel')}</label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldErrors.category_id ? 'input-error' : ''}
              >
                <option value="">{t('product.categoryPlaceholder')}</option>
                {mainCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{t('categories.' + cat.slug, cat.name)}</option>
                ))}
              </select>
              {fieldErrors.category_id && <span className="error-text">{fieldErrors.category_id}</span>}
            </div>
          )}

          {/* Sous-categorie */}
          {subcategories.length > 0 && (
            <div className="form-group">
              <label>{isVehicle ? t('vehicle.bodyType') : t('product.subcategoryLabel')}</label>
              <select
                name="subcategory_id"
                value={formData.subcategory_id}
                onChange={handleChange}
              >
                <option value="">{t('product.subcategoryPlaceholder')}</option>
                {subcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>{t('categories.' + sub.slug, sub.name)}</option>
                ))}
              </select>
            </div>
          )}

          {/* === VEHICLE-SPECIFIC FIELDS === */}
          {isVehicle && (
            <>
              {/* Section 1: Identite du vehicule */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#128663;</span>
                  {t('vehicle.sectionIdentity')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.make')} *</label>
                    <select
                      name="vehicle_make"
                      value={formData.vehicle_make}
                      onChange={handleChange}
                    >
                      <option value="">{t('vehicle.selectMake')}</option>
                      {carMakes.map(make => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                      <option value="Autre">{t('vehicle.otherMake')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.model')}</label>
                    <input
                      type="text"
                      name="vehicle_model"
                      value={formData.vehicle_model}
                      onChange={handleChange}
                      placeholder={t('vehicle.modelPlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.year')}</label>
                    <input
                      type="number"
                      name="vehicle_year"
                      value={formData.vehicle_year}
                      onChange={handleChange}
                      min="1950"
                      max={new Date().getFullYear() + 1}
                      placeholder={t('vehicle.yearPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.mileage')}</label>
                    <input
                      type="number"
                      name="vehicle_mileage"
                      value={formData.vehicle_mileage}
                      onChange={handleChange}
                      min="0"
                      placeholder={t('vehicle.mileagePlaceholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Motorisation */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#9881;</span>
                  {t('vehicle.sectionEngine')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.fuel')}</label>
                    <select
                      name="vehicle_fuel"
                      value={formData.vehicle_fuel}
                      onChange={handleChange}
                    >
                      <option value="">{t('vehicle.selectFuel')}</option>
                      <option value="essence">{t('vehicle.fuelEssence')}</option>
                      <option value="diesel">{t('vehicle.fuelDiesel')}</option>
                      <option value="hybride">{t('vehicle.fuelHybride')}</option>
                      <option value="electrique">{t('vehicle.fuelElectrique')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.transmission')}</label>
                    <select
                      name="vehicle_transmission"
                      value={formData.vehicle_transmission}
                      onChange={handleChange}
                    >
                      <option value="">{t('vehicle.selectTransmission')}</option>
                      <option value="manuelle">{t('vehicle.transmissionManuelle')}</option>
                      <option value="automatique">{t('vehicle.transmissionAutomatique')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.power')}</label>
                    <input
                      type="number"
                      name="vehicle_power_cv"
                      value={formData.vehicle_power_cv}
                      onChange={handleChange}
                      min="0"
                      placeholder={t('vehicle.powerPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.displacement')}</label>
                    <input
                      type="number"
                      name="vehicle_displacement"
                      value={formData.vehicle_displacement}
                      onChange={handleChange}
                      min="0"
                      placeholder={t('vehicle.displacementPlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('vehicle.drive')}</label>
                  <select
                    name="vehicle_drive"
                    value={formData.vehicle_drive}
                    onChange={handleChange}
                  >
                    <option value="">-</option>
                    <option value="avant">{t('vehicle.driveFront')}</option>
                    <option value="arriere">{t('vehicle.driveRear')}</option>
                    <option value="4x4">{t('vehicle.drive4x4')}</option>
                  </select>
                </div>
              </div>

              {/* Section 3: Habitacle */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#128186;</span>
                  {t('vehicle.sectionInterior')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.doors')}</label>
                    <select
                      name="vehicle_doors"
                      value={formData.vehicle_doors}
                      onChange={handleChange}
                    >
                      <option value="">-</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.seats')}</label>
                    <select
                      name="vehicle_seats"
                      value={formData.vehicle_seats}
                      onChange={handleChange}
                    >
                      <option value="">-</option>
                      <option value="2">2</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="7">7</option>
                      <option value="9">9</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.exteriorColor')}</label>
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      placeholder={t('vehicle.colorPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.interiorColor')}</label>
                    <input
                      type="text"
                      name="vehicle_color_interior"
                      value={formData.vehicle_color_interior}
                      onChange={handleChange}
                      placeholder={t('vehicle.interiorColorPlaceholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Section 4: Etat & controle */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#128196;</span>
                  {t('vehicle.sectionDocuments')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.inspection')}</label>
                    <input
                      type="date"
                      name="vehicle_inspection_date"
                      value={formData.vehicle_inspection_date}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.vin')}</label>
                    <input
                      type="text"
                      name="vehicle_vin"
                      value={formData.vehicle_vin}
                      onChange={handleChange}
                      placeholder={t('vehicle.vinPlaceholder')}
                      maxLength="20"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* === STANDARD PRODUCT FIELDS (hidden for vehicles) === */}
          {!isVehicle && (
            <>
              {/* Marque et Modele */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.brand')}</label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder={t('product.brandPlaceholder')}
                  />
                </div>

                {isTechCategory() && (
                  <div className="form-group">
                    <label>{t('common.model')}</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      placeholder={t('product.modelPlaceholder')}
                    />
                  </div>
                )}
              </div>

              {/* Taille (pour vetements) */}
              {isClothingCategory() && (
                <div className="form-group">
                  <label>{t('common.size')}</label>
                  <input
                    type="text"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    placeholder={t('product.sizePlaceholder')}
                  />
                </div>
              )}

              {/* Couleur et Matiere */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.color')}</label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder={t('product.colorPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('product.materialLabel')}</label>
                  <input
                    type="text"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    placeholder={t('product.materialPlaceholder')}
                  />
                </div>
              </div>

              {/* Annee (pour tech) */}
              {isTechCategory() && (
                <div className="form-group">
                  <label>{t('common.year')}</label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    min="1990"
                    max={new Date().getFullYear()}
                    placeholder={t('product.yearPlaceholder')}
                  />
                </div>
              )}
            </>
          )}

          {/* Etat */}
          <div className="form-group">
            <label>{t('product.conditionLabel')}</label>
            <select
              name="condition"
              value={formData.condition}
              onChange={handleChange}
            >
              <option value="neuf_etiquette">{t('product.conditionNeuf')}</option>
              <option value="neuf_sans_etiquette">{t('product.conditionNeufSans')}</option>
              <option value="tres_bon">{t('product.conditionTresBon')}</option>
              <option value="bon">{t('product.conditionBon')}</option>
              <option value="satisfaisant">{t('product.conditionSatisfaisant')}</option>
            </select>
          </div>

          {/* Prix */}
          <div className="form-row">
            <div className={`form-group ${getFieldClass('price')}`}>
              <label>{t('product.priceLabel')}</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                onBlur={handleBlur}
                min={isVehicle ? '500' : '0.50'}
                step={isVehicle ? '100' : '0.50'}
                placeholder={isVehicle ? t('vehicle.pricePlaceholder') : t('product.pricePlaceholder')}
                className={fieldErrors.price ? 'input-error' : ''}
              />
              {fieldErrors.price && <span className="error-text">{fieldErrors.price}</span>}
            </div>

            {!isVehicle && (
              <div className="form-group">
                <label>{t('product.originalPriceLabel')}</label>
                <input
                  type="number"
                  name="original_price"
                  value={formData.original_price}
                  onChange={handleChange}
                  min="0"
                  step="0.50"
                  placeholder={t('product.originalPricePlaceholder')}
                />
                <small className="field-hint">{t('product.originalPriceNote')}</small>
              </div>
            )}
          </div>

          {/* Affichage du prix total avec frais (masque pour voitures) */}
          {priceDetails && !priceDetails.isVehicle && (
            <div className="price-breakdown">
              <h4>{t('product.priceDetail')}</h4>
              <div className="price-line">
                <span>{t('product.itemPrice', { price: priceDetails.price.toFixed(2) }).split(':')[0]}</span>
                <span>{priceDetails.price.toFixed(2)} CHF</span>
              </div>
              <div className="price-line">
                <span>{t('product.buyerProtection')}</span>
                <span>{priceDetails.insurance.toFixed(2)} CHF</span>
              </div>
              <div className="price-line">
                <span>{t('product.serviceFee')}</span>
                <span>{priceDetails.commission.toFixed(2)} CHF</span>
              </div>
              <div className="price-line total">
                <span>{t('product.totalPrice')}</span>
                <span>{priceDetails.total.toFixed(2)} CHF</span>
              </div>
            </div>
          )}

          {/* Description */}
          <div className={`form-group ${getFieldClass('description')}`}>
            <label>{t('product.descriptionLabel')}</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              onBlur={handleBlur}
              minLength="10"
              rows={isVehicle ? 8 : 5}
              placeholder={isVehicle ? t('vehicle.descriptionPlaceholder') : t('product.descriptionPlaceholder')}
              className={fieldErrors.description ? 'input-error' : ''}
            />
            {fieldErrors.description && <span className="error-text">{fieldErrors.description}</span>}
          </div>

          {/* Images */}
          <div className={`form-group ${fieldErrors.images ? 'field-error' : ''}`}>
            <label>{isVehicle ? t('vehicle.photosLabel', { count: images.length, max: maxImages }) : t('product.photosLabel', { count: images.length })}</label>

            {images.length < maxImages && (
              <div className="image-upload-container">
                <input
                  type="file"
                  multiple
                  onChange={handleImageChange}
                  className="image-input-hidden"
                />
                <div className="add-photo-btn">
                  <span className="add-photo-icon">📷</span>
                  <span>{images.length === 0 ? t('product.addPhotos') : t('product.addPhoto')}</span>
                </div>
              </div>
            )}
            {fieldErrors.images && <span className="error-text">{fieldErrors.images}</span>}
            {compressing && (
              <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
                Optimisation des images en cours...
              </div>
            )}

            {imagePreviews.length > 0 && (
              <div className="image-previews">
                <p className="preview-instruction">{t('product.touchToSetMain')}</p>
                <div className="preview-grid">
                  {imagePreviews.map((preview, index) => (
                    <div
                      key={index}
                      className={`preview-item ${index === mainImageIndex ? 'main-image' : ''}`}
                      onClick={() => handleSetMainImage(index)}
                    >
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      {index === mainImageIndex && (
                        <span className="main-badge">{t('product.mainPhoto')}</span>
                      )}
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(index);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Localisation */}
          <div className="links-section">
            <h3>{t('product.locationLabel')}</h3>
            <div className="form-group">
              <label>{t('product.streetLabel')}</label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                placeholder={t('product.streetPlaceholder')}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('common.postalCode')}</label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder={t('product.postalCodePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('common.city')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder={t('product.cityPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Options de livraison (masque pour voitures) */}
          {!isVehicle && (
            <div className="guest-list-section">
              <h3>{t('product.shippingLabel')}</h3>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="shipping_available"
                    checked={formData.shipping_available}
                    onChange={handleChange}
                  />
                  {t('product.shippingAvailable')}
                </label>
              </div>

              {formData.shipping_available && (
                <div className="form-group">
                  <label>{t('product.shippingCost')}</label>
                  <input
                    type="number"
                    name="shipping_price"
                    value={formData.shipping_price}
                    onChange={handleChange}
                    min="0"
                    step="0.50"
                    placeholder={t('product.shippingDefault')}
                  />
                </div>
              )}

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="local_pickup"
                    checked={formData.local_pickup}
                    onChange={handleChange}
                  />
                  {t('product.handDelivery')}
                </label>
              </div>
            </div>
          )}

          {/* Info remise en main propre pour voitures */}
          {isVehicle && (
            <div className="guest-list-section">
              <h3>{t('product.shippingLabel')}</h3>
              <p style={{ color: '#666', fontSize: '0.9em' }}>{t('vehicle.pickupOnly')}</p>
            </div>
          )}

          <button type="submit" className="btn-primary" disabled={loading || compressing}>
            {compressing ? 'Optimisation des images...' : loading ? t('product.creating') : (isVehicle ? t('vehicle.publishButton') : t('product.publishButton'))}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateProduct;
